import { FC, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Button, InlineMessages, InlineMessagesType, Modal } from '@marriott/mi-ui-library';
import { HotelMediaContent } from '@marriott/mi-groups-graphql';
import { PropertyCardProps } from './PropertyCard.types';
import { StyledPropertyCard } from './PropertyCard.styles';
import { RoomCard } from './RoomCard';
import { PreApprovedImage } from '../../ImageTray/PreApprovedImages/PreApprovedImages.types';
import { PropertyCard as CommonPropertyCard } from '../../../../../../molecules/PropertyCard';
import { getFormattedDate, formatAmount, formatUTCDate, getCategoryImages } from '../../../../../../utils';
import { PropertyQuickViewModal } from '../../../../../../molecules';
import { isGroupType } from '../../../../../utils/reslink';
import { useDttStore } from '../../../../../../store';
import { useReslinkAssociateStore } from '../../../../../../admin/store';

export const PropertyCard: FC<PropertyCardProps> = props => {
  const { propertyLabels, propertyData, removeHotelLabels, hasError, remove, update, onMoveUp, onMoveDown, onEdit } =
    props;
  const {
    swapUp,
    swapDown,
    of,
    marshaCodeLabel,
    propertyIdLabel,
    miniHotelLabel,
    miniHotelGroupCodeLabel,
    clusterCodeLabel,
    corporateIdLabel,
    hIndicatorWarning,
    hIndicatorWarningDtt,
    removeHotel,
    mediaLabels,
    viewHotelDetails,
    bookBy,
    rates,
    nightlyRates,
    maxGuests,
    to,
    perNight,
    edit,
    groupCodeLabel,
    inventoryCodeLabel,
    roomTypeLabel,
    remove: removeLabel,
    undo,
    apiFailure,
  } = propertyLabels;
  const {
    id,
    cardNumber,
    totalCards,
    isHousingProtected,
    marshaCode,
    miniHotelCode,
    groupBlockCode,
    clusterCode,
    corporateId,
    reslinkType,
    propertyId,
    media,
    brand,
    hotelName,
    reviewsAndDistance,
    description,
    bookByDate,
    fromDate,
    overriddenStartDate,
    toDate,
    overriddenEndDate,
    maxOccupancy,
    overriddenMaxOccupancy,
    minRate,
    maxRate,
    currency,
    isEdited,
    roomsData,
    isMarshaProperty = true,
    propertyIndex,
  } = propertyData;
  const { heading, subHeading, message, cancelCtaLabel, confirmCtaLabel } = removeHotelLabels;

  const isGroup = isGroupType(reslinkType);

  const { isDtt } = useDttStore();
  const { removeEventTrackingData, isPropertyExist, incrementPropertyCount, setPropertyImages, removePropertyImages } =
    useReslinkAssociateStore();

  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
  const [showQuickViewModal, setShowQuickViewModal] = useState<boolean>(false);

  useEffect(() => {
    if (!hasError) {
      if (isPropertyExist(propertyId)) {
        incrementPropertyCount(propertyId);
      } else {
        setPropertyImages(propertyId, getPreApprovedImages(media));
      }
    }
  }, []);

  const removeProperty = () => {
    remove(cardNumber - 1);
    setShowRemoveModal(false);
    propertyIndex && removeEventTrackingData(propertyIndex);
    !hasError && removePropertyImages(propertyId);
  };

  const updateRoomSummary = (roomIndex: number, isRemoved: boolean) => {
    const roomsData = propertyData.roomsData.map((roomData, index) =>
      index === roomIndex ? { ...roomData, isRemoved } : roomData
    );

    const ratesAndMaxOccupancy = roomsData
      .filter(roomData => !roomData.isRemoved)
      .reduce(
        (acc, item) => {
          if (typeof item.roomRate === 'number') {
            acc.minRoomRate = Math.min(acc.minRoomRate, item.roomRate);
            acc.maxRoomRate = Math.max(acc.maxRoomRate, item.roomRate);
          }
          if (typeof item.maxOccupancy === 'number') {
            acc.maxOccupancy = Math.max(acc.maxOccupancy, item.maxOccupancy);
          }
          return acc;
        },
        { minRoomRate: Infinity, maxRoomRate: -Infinity, maxOccupancy: -Infinity }
      );

    let maxOccupancy: number | undefined;

    if (propertyData.isEdited) {
      maxOccupancy = propertyData.maxOccupancy;
    } else if (isFinite(ratesAndMaxOccupancy.maxOccupancy)) {
      maxOccupancy = ratesAndMaxOccupancy.maxOccupancy;
    } else {
      maxOccupancy = undefined;
    }

    update(cardNumber - 1, {
      ...propertyData,
      maxOccupancy,
      minRate: isFinite(ratesAndMaxOccupancy.minRoomRate) ? ratesAndMaxOccupancy.minRoomRate : '-',
      maxRate: isFinite(ratesAndMaxOccupancy.maxRoomRate) ? ratesAndMaxOccupancy.maxRoomRate : '-',
      roomsData,
    });
  };

  const getPreApprovedImages = (media: HotelMediaContent): PreApprovedImage[] => {
    const primaryImages = getCategoryImages(media?.primaryImage);
    const hotelViewImages = getCategoryImages(media?.photoGallery?.hotelView);
    const eventsAndMeetingsImages = getCategoryImages(media?.photoGallery?.eventsAndMeetings);
    const preApprovedImages = [...primaryImages, ...hotelViewImages, ...eventsAndMeetingsImages];
    return Array.from(new Map(preApprovedImages.map(img => [img.name, img])).values());
  };

  return (
    <StyledPropertyCard hasError={hasError}>
      <div
        className="d-flex flex-row flex-wrap align-items-center p-2 reslink-property-card-header"
        data-testid="property-card-id"
      >
        <div className="d-flex align-items-center">
          <Button ariaLabel={swapUp} className="transparent-icon" isDisabled={cardNumber === 1} callback={onMoveUp}>
            <span className={clsx('icon-arrow-up', cardNumber === 1 && 'icon-disabled')}></span>
          </Button>
          <span className="align-self-stretch my-1 seperator"></span>
          <Button
            ariaLabel={swapDown}
            className="transparent-icon"
            isDisabled={cardNumber === totalCards}
            callback={onMoveDown}
          >
            <span className={clsx('icon-arrow-down', cardNumber === totalCards && 'icon-disabled')}></span>
          </Button>
          <span className="t-font-xs">
            {cardNumber} {of} {totalCards}
          </span>
        </div>
        <div className={clsx('d-flex flex-column ml-md-0', !isHousingProtected && 'col-auto col-md-5 col-lg-auto')}>
          <p className="mb-0">
            <span className="t-font-xs">{isDtt ? propertyIdLabel : marshaCodeLabel} </span>
            <span className="t-label-inverse-alt-xs">{isDtt ? propertyId : marshaCode}</span>
          </p>
          {isDtt ? (
            <p className="mb-0">
              <span className="t-font-xs">{isGroup ? miniHotelGroupCodeLabel : corporateIdLabel} </span>
              <span className="t-label-inverse-alt-xs">{isGroup ? groupBlockCode : corporateId}</span>
            </p>
          ) : (
            <p className="mb-0">
              <span className="t-font-xs">{isGroup ? miniHotelLabel : clusterCodeLabel} </span>
              <span className="t-label-inverse-alt-xs">{isGroup ? miniHotelCode : clusterCode}</span>
            </p>
          )}
        </div>
        {isGroup && isHousingProtected ? (
          <div className="my-3 my-lg-0 p-0 col-md-12 col-lg-5 col-xl-6">
            <InlineMessages
              title={isDtt ? hIndicatorWarningDtt : hIndicatorWarning}
              type={InlineMessagesType.Warning}
            />
          </div>
        ) : null}
        <Button
          className={clsx(
            'mb-2 mb-lg-0 t-font-s m-button-primary remove-hotel',
            isHousingProtected ? 'ml-auto mr-lg-0 mt-0' : 'mx-auto mr-md-0 mt-md-0 mt-2'
          )}
          callback={() => setShowRemoveModal(true)}
        >
          <span className="mr-1">{removeHotel}</span>
          <span className="icon-clear"></span>
        </Button>
      </div>
      <div className="reslink-property-card">
        <CommonPropertyCard
          id={id}
          media={media}
          mediaLabels={mediaLabels}
          brand={brand}
          propertyName={{
            title: hotelName,
            onClick: () => {
              setShowQuickViewModal(true);
            },
          }}
          reviewsAndDistance={reviewsAndDistance}
          description={description}
          viewDetailsCta={{
            label: viewHotelDetails,
            onClick: () => {
              setShowQuickViewModal(true);
            },
          }}
        />
        {isGroup ? (
          <>
            <div className="row justify-content-between py-4 px-3 m-0 room-details-summary">
              <div className="row col-12 col-lg-9 room-availabilty-summary">
                <div className="col-3 col-md-2">
                  <span className="d-block t-font-xs">{bookBy}</span>
                  <span className="t-label-inverse-alt-xs">
                    {bookByDate ? getFormattedDate(formatUTCDate(bookByDate), 'dateWithMonthNoAndShortYear') : ''}
                  </span>
                </div>
                <div className="col-5 col-md-3">
                  <span className="d-block t-font-xs">{rates}</span>
                  {fromDate && toDate ? (
                    <span className="t-label-inverse-alt-xs">
                      {getFormattedDate(formatUTCDate(overriddenStartDate || fromDate), 'dateWithMonthNoAndShortYear')}{' '}
                      {to} {getFormattedDate(formatUTCDate(overriddenEndDate || toDate), 'dateWithMonthNoAndShortYear')}
                    </span>
                  ) : null}
                </div>
                <div className="col-4 col-md-2">
                  <span className="d-block t-font-xs">{maxGuests}</span>
                  <span className="t-label-inverse-alt-xs">{overriddenMaxOccupancy || maxOccupancy}</span>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-end col-12 col-lg-3 mt-3 mt-md-0 room-rates-summary">
                <div className="mr-3">
                  <span className="m-price">{typeof minRate === 'number' ? formatAmount(minRate) : minRate}</span>
                  {minRate !== maxRate ? (
                    <>
                      <span className="t-label-inverse-alt-xs"> {to} </span>
                      <span className="m-price">{typeof maxRate === 'number' ? formatAmount(maxRate) : maxRate}</span>
                    </>
                  ) : null}
                  {minRate && maxRate && minRate !== '-' ? (
                    <span className="t-font-xs">
                      {' '}
                      {currency}
                      {perNight}
                    </span>
                  ) : null}
                </div>
                <Button className="edit-link" ariaLabel={edit} callback={onEdit}>
                  <span className="t-font-xs edit-label">{edit}</span>
                  <span className="icon-edit"></span>
                </Button>
              </div>
              {isEdited && <div className="edit-indicator"></div>}
            </div>
            {roomsData.map((roomData, index) => {
              return (
                <RoomCard
                  key={roomData.groupCode || roomData.inventoryTypeCode}
                  labels={{
                    rates: isMarshaProperty ? rates : nightlyRates,
                    to,
                    maxGuests,
                    groupCodeLabel,
                    inventoryCodeLabel,
                    roomTypeLabel,
                    perNight,
                    remove: removeLabel,
                    undo,
                  }}
                  data={roomData}
                  roomIndex={index}
                  hasError={hasError}
                  isMarshaProperty={isMarshaProperty}
                  onRemove={roomIndex => updateRoomSummary(roomIndex, true)}
                  onUndoRemove={roomIndex => updateRoomSummary(roomIndex, false)}
                />
              );
            })}
          </>
        ) : null}

        {showRemoveModal ? (
          <Modal
            handleBlur={true}
            role="dialog"
            labelledBy={heading}
            modalId="removePropertyModal"
            disableScrollOnBody={true}
            show={showRemoveModal}
            onClose={() => setShowRemoveModal(false)}
            popupOpenState={showRemoveModal}
            setPopupOpenState={setShowRemoveModal}
          >
            <Modal.Header labelText={heading} popupHeaderOnCLoseFunc={() => setShowRemoveModal(false)}></Modal.Header>
            <Modal.Body className="custom-scrollbar">
              <p>{subHeading.replace('{hotelName}', `"${hotelName}"`)}</p>
              <p>{message}</p>
            </Modal.Body>
            <Modal.FooterGeneric
              fadeEffect={true}
              actions={
                <>
                  <Button
                    className="m-button-m m-button-secondary"
                    ariaLabel={cancelCtaLabel}
                    callback={() => setShowRemoveModal(false)}
                  >
                    {cancelCtaLabel}
                  </Button>
                  <Button className="m-button-m m-button-primary" ariaLabel={confirmCtaLabel} callback={removeProperty}>
                    {confirmCtaLabel}
                  </Button>
                </>
              }
            ></Modal.FooterGeneric>
          </Modal>
        ) : null}
      </div>

      {showQuickViewModal ? (
        <PropertyQuickViewModal
          show={showQuickViewModal}
          visibilityConfig={{
            capacity: false,
            equipmentAndServices: false,
            footer: false,
          }}
          labels={{ ...propertyLabels, ...propertyLabels.mediaLabels }}
          propertyId={propertyId}
          error={apiFailure}
          onCloseModal={() => {
            setShowQuickViewModal(false);
          }}
        />
      ) : null}
    </StyledPropertyCard>
  );
};
